import { useState } from 'react';
import { addWireDays } from '@forecastify/shared';
import { useSession } from '../../session';
import { windowDays } from './range-control';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ HOW A DASHBOARD WAS LAST LOOKED AT — 3 Nov 2026, the product owner.  │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Every dashboard opened on its code default whatever somebody had set the last time: the week or
 * day mode, how long the window was, the leave chart's zoom, the project tab's grouping. Kept in
 * the BROWSER, per workspace and per member — a view choice like a report's hidden columns, not
 * something a colleague should inherit (`decisions.md` DASH-1).
 *
 * ⚠️⚠️ **THE WINDOW'S LENGTH, NEVER ITS DATES.** A window saved as *1–30 September* reopens on a
 * month nobody is looking at any more; one saved as *sixteen weeks* reopens on sixteen weeks from
 * the screen's own default start, which is relative to today.
 *
 * ⚠️ **Storage that fails, or holds rubbish, is the default** — every field is checked on the way
 * in, and a private window that throws on `localStorage` simply remembers nothing.
 */
export type DashboardKey = 'logging' | 'leave' | 'resource' | 'utilization' | 'projectResource';

export interface DashboardPrefs {
  readonly mode?: 'week' | 'day';
  /** The window's length in days, inclusive of both ends. */
  readonly days?: number;
  readonly zoom?: 'week' | 'month' | 'quarter';
  readonly grouping?: 'people' | 'items';
}

/**
 * ⚠️ **`.view` at the end** so the key cannot collide with the filters the same dashboard
 * remembers under `forecastify.dash.<workspace>.<member>.<dashboard>` — two writers, one key, and
 * each would erase the other.
 */
export function prefsKey(workspaceId: string, memberId: string, dashboard: DashboardKey): string {
  return `forecastify.dash.${workspaceId}.${memberId}.${dashboard}.view`;
}

/** The longest window any dashboard draws — three years of days. Anything longer is rubbish. */
const MAX_DAYS = 1100;

function parsePrefs(text: string | null): DashboardPrefs {
  if (text === null) return {};
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    return {};
  }
  if (typeof raw !== 'object' || raw === null) return {};
  const value = raw as Record<string, unknown>;
  const out: {
    mode?: 'week' | 'day';
    days?: number;
    zoom?: 'week' | 'month' | 'quarter';
    grouping?: 'people' | 'items';
  } = {};
  if (value.mode === 'week' || value.mode === 'day') out.mode = value.mode;
  if (
    typeof value.days === 'number' &&
    Number.isInteger(value.days) &&
    value.days >= 1 &&
    value.days <= MAX_DAYS
  ) {
    out.days = value.days;
  }
  if (value.zoom === 'week' || value.zoom === 'month' || value.zoom === 'quarter') {
    out.zoom = value.zoom;
  }
  if (value.grouping === 'people' || value.grouping === 'items') out.grouping = value.grouping;
  return out;
}

export function readPrefs(
  workspaceId: string,
  memberId: string,
  dashboard: DashboardKey,
): DashboardPrefs {
  try {
    return parsePrefs(globalThis.localStorage.getItem(prefsKey(workspaceId, memberId, dashboard)));
  } catch {
    return {};
  }
}

/** Merges, so the zoom saved a minute ago survives a window change saved now. */
export function writePrefs(
  workspaceId: string,
  memberId: string,
  dashboard: DashboardKey,
  patch: DashboardPrefs,
): void {
  try {
    const next = { ...readPrefs(workspaceId, memberId, dashboard), ...patch };
    globalThis.localStorage.setItem(
      prefsKey(workspaceId, memberId, dashboard),
      JSON.stringify(next),
    );
  } catch {
    /* A dashboard that cannot remember is not a dashboard that should fail. */
  }
}

/** Remember a window's LENGTH — its dates are deliberately thrown away. */
export function saveLength(
  save: (patch: DashboardPrefs) => void,
  range: { readonly from: string; readonly to: string },
): void {
  const days = windowDays(range);
  if (days !== null) save({ days });
}

/**
 * The default window with the remembered LENGTH — the default's own start, which is relative to
 * today, and as many days after it as were last on screen.
 */
export function withLength(
  range: { readonly from: string; readonly to: string },
  days: number | undefined,
): { from: string; to: string } {
  if (days === undefined) return { from: range.from, to: range.to };
  return { from: range.from, to: addWireDays(range.from, days - 1) ?? range.to };
}

/**
 * ⚠️ **Read ONCE, on mount**, and written on every change. Re-reading per render would have the
 * screen fight its own state; a workspace switch remounts the route anyway.
 */
export function useDashboardPrefs(dashboard: DashboardKey): {
  readonly prefs: DashboardPrefs;
  readonly save: (patch: DashboardPrefs) => void;
} {
  const session = useSession();
  const workspaceId = session.workspaceId;
  const memberId =
    session.workspaces.find((one) => one.id === session.workspaceId)?.memberId ?? null;
  const [prefs] = useState<DashboardPrefs>(() =>
    workspaceId === null || memberId === null ? {} : readPrefs(workspaceId, memberId, dashboard),
  );
  return {
    prefs,
    save: (patch) => {
      if (workspaceId === null || memberId === null) return;
      writePrefs(workspaceId, memberId, dashboard, patch);
    },
  };
}
